import { WireTypes, visibilityTypeToAllowedVisibilities } from '@flakiness/server/common/wireTypes.js';
import { LitElement, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import ms from 'ms';
import { docsUrl } from '../platformUrl.js';
import { linkStyles } from './cssstyles.js';

const INFINITY = html`<sl-icon name=infinity></sl-icon>`;
const DASH = html`<sl-icon name=dash-lg></sl-icon>`;
const CHECK = html`<sl-icon class=check-icon name=check-circle-fill></sl-icon>`;
const CROSS = html`<sl-icon class=cross-icon name=x-lg></sl-icon>`;

@customElement('product-plans-view')
class ProductPlansView extends LitElement {
  @property({ attribute: false }) plans?: WireTypes.ProductPlan[];
  @property({ attribute: false }) selectedPlanId?: string;
  @property({ attribute: false }) subscription?: WireTypes.Subscription;
  @property({ attribute: false }) trialUsedAtSeconds?: number;
  @property({ attribute: false }) loading?: boolean;

  @property({ type: Boolean, attribute: 'no-enterprise' }) noEnterpriseColumn?: boolean;
  @property({ type: Boolean, attribute: 'no-subscribe' }) noSubscribeButtons?: boolean;
  @property({ type: Boolean, attribute: 'plan-selector' }) planSelect?: boolean;
  @property({ type: Boolean, attribute: 'show-del' }) showTrashButton?: boolean;
  @property({ type: Boolean, attribute: 'show-edit' }) showEditButton?: boolean;
  @property({ type: Boolean, attribute: 'show-dup' }) showDupButton?: boolean;
  @property({ type: Boolean, attribute: 'show-plan-id' }) showPlanId?: boolean;
  @property({ attribute: 'edit-org-slug' }) editOrgSlug?: string;

  private _formatPrice(plan: WireTypes.ProductPlan): TemplateResult<1> {
    const renderAmount = (price: number, subtitle: string) => html`
      <v-box style="gap: var(--sl-spacing-2x-small)">
        <div class=price-amount>
          <span class=price-currency>$</span><span class=price-value>${price}</span><span class=price-unit>USD</span><span class=price-period>/ month</span>
        </div>
        ${subtitle ? html`<div class=price-subtitle>${subtitle}</div>` : nothing}
      </v-box>
    `;
    if (!plan.price)
      return renderAmount(0, '');
    if (plan.billing === 'month' || plan.billing === 'year') {
      const price = plan.billing === 'year' ? Math.floor(plan.price / 12) : plan.price;
      const subtitle = plan.billing === 'year' ? `$${plan.price} billed annually` : 'billed monthly';
      return renderAmount(price, subtitle);
    }
    return html`<strong>$${plan.price}/${plan.billing}</strong>`;
  }

  private _formatTrial(plan: WireTypes.ProductPlan): TemplateResult<1> {
    const trialEnd = this.subscription?.trialEnd ?? 0;
    const isRunningTrial = Date.now() < trialEnd;
    if (plan.id === this.subscription?.plan.id) {
      return isRunningTrial ? html`
        <sl-tooltip>
          <span slot=content>Until <sl-format-date month="long" day="numeric" year="numeric" date=${new Date(trialEnd).toISOString()}></sl-format-date></span>
          <span>${Math.ceil((trialEnd - Date.now()) / ms('1 day'))} days left</span>
        </sl-tooltip>` : html`${plan.trialDays ? 'Free trial has ended' : DASH}
        `;
    }
    if (plan.trialDays && this.trialUsedAtSeconds) {
      return html`
        <v-box class="trial-status trial-unavailable">
          <span><s>${plan.trialDays} days</s></span>
          <span class=trial-note>Trial already used</span>
        </v-box>
      `;
    }
    if (plan.trialDays)
      return html`${plan.trialDays} days`;
    return DASH;
  }

  private _formatStorage(plan: WireTypes.ProductPlan): TemplateResult<1> {
    if (plan.storage === undefined)
      return INFINITY;
    if (plan.id === this.subscription?.plan.id) {
      const usedGB = Math.round(this.subscription.usedStorageBytes / 1000 / 1000 / 1000 * 100) / 100;
      return html`${usedGB.toLocaleString()}GB out of ${plan.storage}GB`
    }
    return html`${plan.storage}GB`;
  }

  private _renderActionButton(plan: WireTypes.ProductPlan): TemplateResult<1> {
    if (this.planSelect) {
      return html`
        <sl-button ?disabled=${plan.id === this.selectedPlanId} @click=${() => {
          this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-select', {
            detail: plan
          }));
        }}>Select plan</sl-button>
      `
    }
    if (this.subscription?.plan.id === plan.id) {
      // Cancellation pending — render a warning badge + Undo button.
      if (this.subscription.cancelAt) {
        const cancelDate = new Date(this.subscription.cancelAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return html`
          <v-box style="gap: var(--sl-spacing-x-small); align-items: stretch;">
            <sl-tag variant=warning size=medium>Cancels ${cancelDate}</sl-tag>
            <sl-button size=small ?disabled=${this.loading} @click=${() => {
              this.dispatchEvent(new CustomEvent('fk-uncancel'));
            }}>Undo cancellation</sl-button>
          </v-box>
        `;
      }
      // Implicit Free has no Stripe customer portal — just show a disabled "Current Plan" badge.
      if (plan.isImplicit)
        return html`<sl-button disabled variant=success>Current Plan</sl-button>`;
      return html`
        <sl-button ?disabled=${this.loading} variant=${
          this.subscription.missingPaymentMethod ? 'warning' :
          this.subscription.hasBillingIssues ? 'danger' : 'success'} @click=${() => {
          this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-select', {
            detail: plan
          }));
        }}>${this.subscription.missingPaymentMethod ? 'Update payment' :
          this.subscription.hasBillingIssues ? 'Payment required' : 'Current Plan'}</sl-button>
      `;
    }
    // Implicit Free row + a paid sub is winding down → show when Free takes over.
    if (plan.isImplicit && this.subscription?.cancelAt) {
      const activeDate = new Date(this.subscription.cancelAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return html`
        <sl-tag variant=success size=medium>Becomes active ${activeDate}</sl-tag>
      `;
    }
    return html`
      <sl-button ?disabled=${this.loading} @click=${() => {
        if (this.subscription) {
          const message = plan.isImplicit
            ? `Your current plan will be cancelled. You'll keep your current benefits until ${new Date(this.subscription.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, then your organization will move to the Free plan.`
            : `This will use your payment on-file to immediately switch your current plan.`;
          const result = confirm(message);
          if (!result)
            return;
        }
        this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-select', {
          detail: plan
        }));
      }}>${this.subscription ? 'Switch' : 'Subscribe'}</sl-button>
    `;
  }

  private _renderFeatures() {
    return html`
      <x-column class=features-column>
        <h-box class=header></h-box>
        <h-box class=price>Price</h-box>
        <h-box class=trial>
          <span>Free Trial</span>
          <fk-help>Each user can only start one free trial across all organizations.</fk-help>
        </h-box>
        <h-box class=storage style="gap: var(--sl-spacing-2x-small);">
          <span>Storage</span>
        </h-box>
        <h-box class=data-retention>Data Retention</h-box>
        <h-box class=public-projects>
          <span>Public projects</span>
          <fk-help href=${docsUrl('/settings/access-control/#project-visibility')}>Anyone can view a public project, even without logging in.</fk-help>
        </h-box>
        <h-box class=private-projects>
          <span>Private projects</span>
          <fk-help href=${docsUrl('/settings/access-control/#project-visibility')}>Only organization members and project collaborators can view a private project.</fk-help>
        </h-box>
        <h-box class=slack-alerts>Slack alerts</h-box>
        <h-box class=self-hosting>Self-hosting</h-box>
        <h-box class=audit-log>Audit log</h-box>
        <h-box class=support>Support</h-box>
        <h-box class=subscribe></h-box>
      </x-column>
    `;
  }

  private _renderEnterprise() {
    return html`
      <x-column class=enterprise-column>
        <h-box class="header">Enterprise</h-box>
        <h-box class=price>Custom Pricing</h-box>
        <h-box class=trial>Custom</h-box>
        <h-box class=storage>Custom</h-box>
        <h-box class=data-retention>Custom</h-box>
        <h-box class=public-projects>${CHECK}</h-box>
        <h-box class=private-projects>${CHECK}</h-box>
        <h-box class=slack-alerts>${CHECK}</h-box>
        <h-box class=self-hosting>${CHECK}</h-box>
        <h-box class=audit-log>${CHECK}</h-box>
        <h-box class=support>Dedicated Slack channel</h-box>
        <h-box class=subscribe>
          <div>Reach out <a href="mailto:sales@flakiness.io">sales@flakiness.io</a></div>
        </h-box>
      </x-column>
    `;
  }

  private _renderPlan(plan: WireTypes.ProductPlan) {
    return html`
      <x-column class=${classMap({
        plan: true,
        selected: plan.id === (this.selectedPlanId ?? this.subscription?.plan.id),
        issues: !!this.subscription?.hasBillingIssues
      })}>
        <h-box class=header style="gap: var(--sl-spacing-2x-small)">
          <div>
            ${plan.name}
            ${this.showPlanId ? html`<sl-copy-button value=${plan.id} copy-label="copy plan id"></sl-copy-button>` : nothing}
          </div>
          ${plan.orgSlug ? html`<sl-tag size=small pill variant=success>special offer</sl-tag>` : nothing}
          ${!plan.isImplicit && (!this.editOrgSlug || plan.orgSlug === this.editOrgSlug) ? html`
            <sl-icon class=btn-dup name=copy @click=${() => {
              this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-duplicate', { detail: plan }));
            }}></sl-icon>
            <sl-icon class=btn-edit name=pencil @click=${() => {
              this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-edit', { detail: plan }));
            }}></sl-icon>
            <sl-icon class=btn-del name=trash @click=${() => {
              this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-remove', { detail: plan }));
            }}></sl-icon>
          ` : nothing}
        </h-box>
        <h-box class=price>
          ${this._formatPrice(plan)}
        </h-box>
        <h-box class=trial>
          ${this._formatTrial(plan)}
        </h-box>
        <h-box class=storage>
          ${this._formatStorage(plan)}
        </h-box>
        <h-box class=data-retention>
          ${plan.maxDataRetentionDays} days
        </h-box>
        <h-box class=public-projects>
          ${visibilityTypeToAllowedVisibilities(plan.projectVisibilityType).includes('public') ? CHECK : CROSS}
        </h-box>
        <h-box class=private-projects>
          ${visibilityTypeToAllowedVisibilities(plan.projectVisibilityType).includes('private') ? CHECK : CROSS}
        </h-box>
        <h-box class=slack-alerts>
          ${plan.premiumFeatures ? CHECK : CROSS}
        </h-box>
        <h-box class=self-hosting>
          ${CROSS}
        </h-box>
        <h-box class=audit-log>
          ${plan.eliteFeatures ? CHECK : CROSS}
        </h-box>
        <h-box class=support>
          ${plan.premiumFeatures ? html`Dedicated Slack channel` : html`<a href="mailto:support@flakiness.io">Standard Support</a>`}
        </h-box>
        <div class=subscribe>
          ${this._renderActionButton(plan)}
        </div>
      </x-column>
    `;
  }

  render() {
    const idToPlan = new Map<string, WireTypes.ProductPlan>();
    for (const plan of this.plans ?? [])
      idToPlan.set(plan.id, plan);
    if (this.subscription)
      idToPlan.set(this.subscription.plan.id, this.subscription.plan);

    const plans = [...idToPlan.values()].toSorted((a, b) => {
      if (a.price !== b.price)
        return a.price - b.price;
      return a.name < b.name ? -1 : 1;
    });

    return html`
      <section>
        ${this._renderFeatures()}
        ${plans.map(plan => this._renderPlan(plan))}
        ${this._renderEnterprise()}

        <div class=add-button>
          <slot></slot>
        </div>
      </section>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
    }

    :host(:not([show-del])) .btn-del {
      display: none;
    }

    :host(:not([show-edit])) .btn-edit {
      display: none;
    }

    :host(:not([show-dup])) .btn-dup {
      display: none;
    }

    :host([no-subscribe]) .subscribe {
      display: none;
    }

    :host([no-enterprise]) .enterprise-column {
      display: none;
    }

    section {
      display: grid;
      grid-template-rows: repeat(12, auto);
      position: relative;

      .add-button {
        position: absolute;
        visibility: hidden;
        top: var(--sl-spacing-2x-small);
        right: var(--sl-spacing-2x-small);
      }

      &:hover .add-button {
        visibility: visible;
      }
    }

    x-column {
      grid-row: 1/-1;
      position: relative;

      display: grid;
      grid-template-rows: subgrid;

      &.selected {
        background-color: var(--sl-color-warning-50);
        border: 1px solid var(--sl-color-warning-300);
        border-radius: var(--sl-border-radius-medium);

        &.issues {
          background-color: var(--sl-color-danger-50);
          border: 1px solid var(--sl-color-danger-300);
        }
      }

      &.plan {
      }

      & > * {
        padding: var(--sl-spacing-small);
        border-bottom: 1px solid var(--fk-color-border);
      }

      &.features-column > * {
        padding-left: 0;
        padding-right: var(--sl-spacing-2x-large);
        border-bottom: 1px solid var(--fk-color-border);
      }

      .subscribe {
        display: flex;
        padding-top: var(--sl-spacing-x-large);
        align-items: center;
        border-bottom: none;

        a {
          color: blue;
        }

        & > * {
          flex: auto;
        }
      }

      .header {
        font-weight: var(--sl-font-weight-bold);
        font-size: var(--sl-font-size-large);
        padding-bottom: var(--sl-spacing-large);
      }

      fk-help {
        font-size: var(--sl-font-size-small);
      }

      .check-icon {
        color: var(--sl-color-success-600);
      }

      .cross-icon {
        color: var(--sl-color-neutral-400);
      }
    }

    .btn-dup, .btn-edit, .btn-del {
      color: var(--sl-color-neutral-300);
      cursor: pointer;
      position: absolute;
      display: none;

      &:hover {
        color: var(--sl-color-neutral-900);
      }
    }

    .btn-dup {
      right: calc(var(--sl-spacing-small) + 2.4em);
    }

    .btn-edit {
      right: calc(var(--sl-spacing-small) + 1.2em);
    }

    .btn-del {
      right: var(--sl-spacing-small);
    }

    x-column:hover .btn-dup, x-column:hover .btn-edit, x-column:hover .btn-del {
      display: block;
    }

    .trial-status {
      gap: var(--sl-spacing-2x-small);
    }

    .trial-unavailable s {
      color: var(--sl-color-neutral-500);
    }

    .trial-note {
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
      line-height: 1.2;
    }

    .price-amount {
      display: flex;
      align-items: baseline;
      gap: var(--sl-spacing-2x-small);
      font-weight: var(--sl-font-weight-normal);
      line-height: 1;
    }

    .price-currency {
      font-size: var(--sl-font-size-medium);
      color: var(--sl-color-neutral-300);
      align-self: flex-start;
    }

    .price-value {
      font-size: var(--sl-font-size-2x-large);
    }

    .price-unit {
      font-size: var(--sl-font-size-medium);
    }

    .price-period {
      font-weight: var(--sl-font-weight-normal);
      color: var(--sl-color-neutral-600);
      margin-left: var(--sl-spacing-2x-small);
      align-self: center;
    }

    .price-subtitle {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
    }
  `];
}
